import { SupportedTpaAppDefIds } from '@wix/a11y-adapters';
import { dsStudioDataModifier, isFastGalleryData } from '@wix/a11y-adapters/studio';
import { useTranslation } from '@wix/fe-essentials/react';
import { assertNotOptional } from '@wix/seo-common-core';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { altTextScreenPageIdAtom } from '../../../atoms/session-atoms';
import { useTpaEntityDataHandlers } from '../../../hooks/tpa/use-tpa-entity-data-handlers';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { PanoramaTransactions } from '../../../panorama/constants';
import { usePanoramaFunctions } from '../../../panorama/use-panorama-functions';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
// Group non-TPA items by componentId for efficient batch processing
const groupItemsByComponent = items => {
  return _.groupBy(items, item => item.componentId ?? item.id);
};

// Group TPA items by appDefId for batch processing per app
const groupItemsByAppDefId = items => {
  return _.groupBy(items, _ref => {
    let {
      tpaData
    } = _ref;
    return (tpaData == null ? void 0 : tpaData.appDefId) ?? SupportedTpaAppDefIds.PRO_GALLERY;
  });
};
export const useStaticPagesAltTextHandlers = () => {
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const {
    setTpaEntityData
  } = useTpaEntityDataHandlers();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const altTextScreenPageId = useAtomValue(altTextScreenPageIdAtom);
  assertNotOptional(altTextScreenPageId);
  const {
    startPanoramaTransaction,
    finishPanoramaTransaction
  } = usePanoramaFunctions();
  const openImageElement = useCallback(nextElement => {
    const {
      id,
      breakpointId,
      tpaData,
      componentId
    } = nextElement;
    const nextCompRef = {
      id: componentId ?? (tpaData == null ? void 0 : tpaData.componentId) ?? id,
      type: getCompRefType(editorHost, breakpointId)
    };
    if (nextCompRef.id) {
      a11yWizardAdapter.selectComponent(nextCompRef, breakpointId);
    }
  }, [a11yWizardAdapter, editorHost]);
  const getEntityName = useCallback(_entityId => {
    return t('accessibility-wizard.verticals-drill-in.static-group-title');
  }, [t]);
  const enabledDecorativeImages = true;
  const setAltText = useCallback(data => {
    const {
      isDecorative,
      isLinked,
      alt,
      id,
      breakpointId,
      builderLabel,
      componentId
    } = data;
    const passesAsDecorative = !!isDecorative && !isLinked;
    const compRef = {
      id: componentId ?? id,
      type: getCompRefType(editorHost, breakpointId)
    };
    const component = a11yWizardAdapter.getComponent(compRef);
    const updatedComponent = dsStudioDataModifier.updateImageProperties(component, {
      alt: passesAsDecorative ? '' : alt,
      builderLabel,
      decorative: passesAsDecorative
    });
    updatedComponent.design && a11yWizardAdapter.setComponentDesign(compRef, updatedComponent.design);
    updatedComponent.data && a11yWizardAdapter.setComponentData(compRef, updatedComponent.data);
  }, [a11yWizardAdapter, editorHost]);
  const setMultipleAltText = useCallback((compRef, component, items) => {
    const updatedComponent = dsStudioDataModifier.updateMultipleImageProperties(component, {
      images: items.map(item => ({
        alt: item.alt,
        id: item.id
      }))
    });
    updatedComponent.design && a11yWizardAdapter.setComponentDesign(compRef, updatedComponent.design);
    updatedComponent.data && a11yWizardAdapter.setComponentData(compRef, updatedComponent.data);
  }, [a11yWizardAdapter]);

  // Process items for a single component (handles FastGallery optimization)
  const processComponentItems = useCallback(async (componentId, items) => {
    const compRef = {
      id: componentId,
      type: getCompRefType(editorHost, items[0].breakpointId)
    };
    const component = a11yWizardAdapter.getComponent(compRef);

    // FastGallery components can be updated in a single batch operation
    const isFastGallery = isFastGalleryData(component);
    if (isFastGallery) {
      setMultipleAltText(compRef, component, items);
    } else {
      // Regular components need individual updates
      items.forEach(data => setAltText(data));
    }
  }, [a11yWizardAdapter, editorHost, setAltText, setMultipleAltText]);
  const setBulkAltText = useCallback(async altTextData => {
    startPanoramaTransaction(PanoramaTransactions.bulkApplyStaticPageAltImage, {
      altTextData
    });
    try {
      // Split items into TPA and non-TPA for different processing paths
      const nonTpaItems = altTextData.filter(item => !item.tpaData);
      const tpaItems = altTextData.filter(item => item.tpaData);

      // Group non-TPA items by component for batch processing
      const itemsByComponent = groupItemsByComponent(nonTpaItems);

      // Process all non-TPA components in parallel
      await Promise.all(Object.entries(itemsByComponent).map(_ref2 => {
        let [componentId, items] = _ref2;
        return processComponentItems(componentId, items);
      }));

      // Group TPA items by appDefId and process in parallel
      const itemsByAppDefId = groupItemsByAppDefId(tpaItems);
      await Promise.all(Object.entries(itemsByAppDefId).map(async _ref3 => {
        let [appDefId, items] = _ref3;
        await setTpaEntityData(appDefId, items);
      }));
    } finally {
      finishPanoramaTransaction(PanoramaTransactions.bulkApplyStaticPageAltImage, {
        altTextData
      });
    }
  }, [finishPanoramaTransaction, processComponentItems, setTpaEntityData, startPanoramaTransaction]);
  return useMemo(() => ({
    enabledDecorativeImages,
    getEntityName,
    openImageElement,
    setBulkAltText
  }), [enabledDecorativeImages, getEntityName, openImageElement, setBulkAltText]);
};
//# sourceMappingURL=use-static-pages-alt-text-handlers.js.map